import { useState } from 'react';

import { useNavigate } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { TextFieldType } from '@cfa/react-components';

import { setSearchSubmittedViaPromptSuggestion } from '@/hooks/exploreSearchAnalytics';
import { removeSearchCacheForTerm } from '@/hooks/useExploreSearch';
import { useLocationChange } from '@/hooks/useLocationChange';
import { incrementSearchCountAtom } from '@/hooks/useSearchSessionTracker';
import { useSmartSearchEnabled } from '@/hooks/useSmartSearchEnabled';

import Searchbar from 'components/Searchbar/Searchbar';
import Constants from 'constants/index';

import AISearchPOC from './AISearchPOC';
import { useSmartSearchPromptSuggestions } from './AISearchPOC/hooks/useSmartSearchPromptSuggestions';

import { useSearchDispatch } from '@/atoms/search';

interface SearchbarSubheaderProps {
  onReset?: () => void;
}

const SearchbarSubheader = ({ onReset }: SearchbarSubheaderProps) => {
  const searchDispatch = useSearchDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const showSmartSearchUi = useSmartSearchEnabled();
  const incrementSearchCount = useSetAtom(incrementSearchCountAtom);
  const [searchValue, setSearchValue] = useState('');
  const [queriedValue, setQueriedValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const { currentPrompt, resetToTop } = useSmartSearchPromptSuggestions({
    enabled: showSmartSearchUi,
    paused: isFocused || isHovered,
  });

  const onChange = (e: React.ChangeEvent<TextFieldType>) => {
    setSearchValue(e.target.value);
  };

  const onClear = () => {
    setSearchValue('');
    setQueriedValue('');
    resetToTop();
    navigate('/');
  };

  const onSubmit = (query: string, meta?: { viaRotatingPrompt?: boolean }) => {
    onReset?.();
    incrementSearchCount(query);
    setSearchValue(query);
    setQueriedValue(query);
    removeSearchCacheForTerm(queryClient, query);
    setSearchSubmittedViaPromptSuggestion(
      query,
      meta?.viaRotatingPrompt ?? false,
    );
    navigate({
      pathname: `/${Constants.ROUTE_PATH_NAMES.SEARCH_PATH_NAME}`,
      search: new URLSearchParams({ query }).toString(),
    });
  };

  useLocationChange((currLocation, prevLocation) => {
    // we need to clear search input when navigating away from the search page
    if (
      prevLocation?.pathname !== currLocation?.pathname &&
      !currLocation?.pathname.includes('search')
    ) {
      setSearchValue('');
      setQueriedValue('');
      resetToTop();
    } else {
      const prevQuery = new URLSearchParams(prevLocation?.search)
        .get('query')
        ?.trim();
      const currQuery = new URLSearchParams(currLocation?.search)
        .get('query')
        ?.trim();
      // new search
      if (prevQuery && currQuery && prevQuery !== currQuery) {
        setSearchValue(currQuery);
        setQueriedValue(currQuery);
      }
      // hits search page directly
      else if (!prevQuery && currQuery) {
        searchDispatch.saveTerm(currQuery);
        setSearchValue(currQuery);
        setQueriedValue(currQuery);
      }
    }
  });

  const fallbackPlaceholder = t('AISearchPOC.searchText');
  const defaultPlaceholder = t('Search.searchForResources');

  return (
    <div className="relative mx-auto w-full">
      <Searchbar
        elevation={1}
        onBlur={() => setIsFocused(false)}
        onChange={onChange}
        onClear={onClear}
        onFocus={() => setIsFocused(true)}
        onPromptHoverChange={setIsHovered}
        onSubmit={onSubmit}
        placeholder={
          showSmartSearchUi ? fallbackPlaceholder : defaultPlaceholder
        }
        promptSuggestion={showSmartSearchUi ? currentPrompt : undefined}
        searchValue={searchValue}
        showRecentSearchDropdown
      />
      {showSmartSearchUi && <AISearchPOC searchValue={queriedValue} />}
    </div>
  );
};

export default SearchbarSubheader;
